// Shared pending-map + socket toasts для create-from-chat (меню + tool create_study_artifact).
// Ключ = jobRef (параллельные create одного artifact не перетирают друг друга).
// Glossary socket даёт materialId — индекс materialId → jobRef.
//
// Fallback: пока есть pending — poll entity status (create-from-chat-reconcile),
// чтобы карточка не залипала, если Socket.IO *:ready был пропущен.

import { toast } from "@studyhub/ui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { prefetchAndSeedNoteGetCache } from "@/components/notes/_api";
import { useSocketEvent } from "@/hooks/use-socket-event";
import {
	applyArtifactCardFailedByJobRef,
	applyArtifactCardReadyByJobRef,
	resolveGlossaryJobRef,
	trackArtifactCardPending,
} from "./artifact-card-live";
import { applyCardLivePatch } from "./card-live";
import type { CreateFromChatArtifact } from "./create-from-chat-options-dialog";
import { fetchCreateFromChatTerminal } from "./create-from-chat-reconcile";
import { clearRichCardEntityPending, reconcileRichCardEntitiesOnce } from "./rich-card-reconcile";

export interface PendingCreateFromChat {
	artifact: CreateFromChatArtifact;
	jobRef: string;
	materialId: string;
}

/** jobRef → pending (notes / flashcards / quiz / glossary). */
const pendingByJobRef = new Map<string, PendingCreateFromChat>();
/** materialId → jobRef для glossary:ready / glossary:failed. */
const glossaryJobByMaterialId = new Map<string, string>();

/** Интервал reconcile poll, пока есть pending create-from-chat. */
const RECONCILE_INTERVAL_MS = 4_000;

export function trackCreateFromChatPending(pending: PendingCreateFromChat): void {
	pendingByJobRef.set(pending.jobRef, pending);
	if (pending.artifact === "glossary") {
		glossaryJobByMaterialId.set(pending.materialId, pending.jobRef);
	}
	trackArtifactCardPending(pending);
}

/**
 * После reload / PS sync: pending cards из content_parts снова попадают в
 * reconcile-poll (иначе map пуст и карточка залипает без socket).
 */
export function seedPendingFromArtifactCards(
	cards: ReadonlyArray<{
		status: string;
		artifact: CreateFromChatArtifact;
		jobRef: string;
		materialId: string;
	}>,
): void {
	for (const card of cards) {
		if (card.status !== "pending") continue;
		if (pendingByJobRef.has(card.jobRef)) continue;
		trackCreateFromChatPending({
			artifact: card.artifact,
			jobRef: card.jobRef,
			materialId: card.materialId,
		});
	}
}

function buildOpenTarget(pending: PendingCreateFromChat): {
	to: string;
	params?: Record<string, string>;
} {
	switch (pending.artifact) {
		case "notes":
			return {
				to: "/notes/$noteId",
				params: { noteId: pending.jobRef },
			};
		case "flashcards":
			return {
				to: "/flashcards/study/$setId",
				params: { setId: pending.jobRef },
			};
		case "quiz":
			return {
				to: "/tests/take/$id",
				params: { id: pending.jobRef },
			};
		case "glossary":
			return { to: "/glossary" };
	}
}

function takePending(jobRef: string): PendingCreateFromChat | null {
	const pending = pendingByJobRef.get(jobRef);
	if (!pending) return null;
	pendingByJobRef.delete(jobRef);
	if (pending.artifact === "glossary") {
		const mapped = glossaryJobByMaterialId.get(pending.materialId);
		if (mapped === jobRef) glossaryJobByMaterialId.delete(pending.materialId);
	}
	return pending;
}

function snapshotPending(): PendingCreateFromChat[] {
	return [...pendingByJobRef.values()];
}

async function reconcilePendingOnce(handlers: {
	resolve: (jobRef: string, opts?: { artifact?: CreateFromChatArtifact; summary?: string }) => void;
	fail: (jobRef: string, artifactHint?: CreateFromChatArtifact) => void;
	isCancelled: () => boolean;
}): Promise<void> {
	const pending = snapshotPending();
	if (pending.length === 0) return;
	for (const item of pending) {
		if (handlers.isCancelled()) return;
		if (!pendingByJobRef.has(item.jobRef)) continue;
		const terminal = await fetchCreateFromChatTerminal(item);
		if (!terminal || handlers.isCancelled()) continue;
		if (!pendingByJobRef.has(item.jobRef)) continue;
		if (terminal.status === "ready") {
			handlers.resolve(item.jobRef, {
				artifact: item.artifact,
				...(terminal.summary ? { summary: terminal.summary } : {}),
			});
		} else {
			handlers.fail(item.jobRef, item.artifact);
		}
	}
}

/**
 * Слушает *:ready / *:failed и показывает toast «Открыть».
 * Монтировать один раз в app shell (`CreateFromChatReadyListener` в __root).
 */
export function useCreateFromChatReadyToasts(): void {
	const { t } = useTranslation("tutor");
	const navigate = useNavigate();
	const qc = useQueryClient();
	const reconcileInFlight = useRef(false);

	const artifactLabel = useCallback(
		(artifact: CreateFromChatArtifact): string => t(`createFrom.${artifact}`),
		[t],
	);

	const resolveByJobRef = useCallback(
		(jobRef: string, opts?: { artifact?: CreateFromChatArtifact; summary?: string }) => {
			const pending = takePending(jobRef);
			const artifact = pending?.artifact ?? opts?.artifact;
			if (artifact) {
				applyArtifactCardReadyByJobRef(jobRef, {
					artifact,
					...(opts?.summary ? { summary: opts.summary } : {}),
				});
			}
			if (!pending) return;
			const target = buildOpenTarget(pending);
			toast.success(t("createFrom.readyTitle", { artifact: artifactLabel(pending.artifact) }), {
				action: {
					label: t("createFrom.readyOpen"),
					onClick: () => {
						if (pending.artifact === "notes") {
							void prefetchAndSeedNoteGetCache(qc, pending.jobRef)
								.catch(() => undefined)
								.then(() => navigate(target));
							return;
						}
						void navigate(target);
					},
				},
			});
		},
		[navigate, qc, t, artifactLabel],
	);

	const failByJobRef = useCallback(
		(jobRef: string, artifactHint?: CreateFromChatArtifact) => {
			const pending = takePending(jobRef);
			applyArtifactCardFailedByJobRef(jobRef);
			if (!pending && !artifactHint) return;
			const artifact = pending?.artifact ?? artifactHint;
			if (!artifact) return;
			toast.error(t("createFrom.failed", { artifact: artifactLabel(artifact) }));
		},
		[t, artifactLabel],
	);

	const resolveRef = useRef(resolveByJobRef);
	resolveRef.current = resolveByJobRef;
	const failRef = useRef(failByJobRef);
	failRef.current = failByJobRef;

	useSocketEvent("note:ready", (p) => resolveRef.current(p.noteId, { artifact: "notes" }));
	useSocketEvent("flashcards:ready", (p) =>
		resolveRef.current(p.setId, {
			artifact: "flashcards",
			summary: typeof p.cardCount === "number" ? String(p.cardCount) : undefined,
		}),
	);
	useSocketEvent("quiz:ready", (p) =>
		resolveRef.current(p.quizId, {
			artifact: "quiz",
			summary: typeof p.questionsCount === "number" ? String(p.questionsCount) : undefined,
		}),
	);
	useSocketEvent("glossary:ready", (p) => {
		const jobRef = glossaryJobByMaterialId.get(p.materialId) ?? resolveGlossaryJobRef(p.materialId);
		if (!jobRef) return;
		resolveRef.current(jobRef, {
			artifact: "glossary",
			summary: typeof p.termsCount === "number" ? String(p.termsCount) : undefined,
		});
	});

	useSocketEvent("note:generation_failed", (p) => failRef.current(p.jobId, "notes"));
	useSocketEvent("flashcards:generation_failed", (p) =>
		failRef.current(p.setId ?? p.jobId, "flashcards"),
	);
	useSocketEvent("quiz:generation_failed", (p) => failRef.current(p.quizId ?? p.jobId, "quiz"));
	useSocketEvent("glossary:failed", (p) => {
		const jobRef = glossaryJobByMaterialId.get(p.materialId) ?? resolveGlossaryJobRef(p.materialId);
		if (!jobRef) return;
		failRef.current(jobRef, "glossary");
	});

	// Action Platform: material / plan / essay rich_cards (id = entity id).
	useSocketEvent("document:ready", (p) => {
		clearRichCardEntityPending(p.materialId);
		applyCardLivePatch(p.materialId, {
			status: "ready",
			summary: "",
			deepLink: `/materials/${p.materialId}`,
		});
	});
	useSocketEvent("document:failed", (p) => {
		clearRichCardEntityPending(p.materialId);
		applyCardLivePatch(p.materialId, {
			status: "failed",
			...(typeof p.errorCode === "string" ? { errorMessage: p.errorCode } : {}),
		});
	});
	useSocketEvent("study-plan:ready", (p) => {
		clearRichCardEntityPending(p.planId);
		applyCardLivePatch(p.planId, {
			status: "ready",
			summary: "",
			// План == курс (обучающая система): каноничный адрес — /courses/<id>.
			deepLink: `/courses/${p.planId}`,
		});
	});
	useSocketEvent("study-plan:generation:failed", (p) => {
		clearRichCardEntityPending(p.planId);
		applyCardLivePatch(p.planId, {
			status: "failed",
			...(typeof p.reason === "string" ? { errorMessage: p.reason } : {}),
		});
	});
	useSocketEvent("essays:ready", (p) => {
		clearRichCardEntityPending(p.essayId);
		applyCardLivePatch(p.essayId, {
			status: "ready",
			summary: typeof p.finalScore === "number" ? String(p.finalScore) : "",
			deepLink: `/tools/essay/${p.essayId}`,
		});
	});
	useSocketEvent("essays:status_changed", (p) => {
		if (p.status !== "failed") return;
		clearRichCardEntityPending(p.essayId);
		applyCardLivePatch(p.essayId, {
			status: "failed",
			...(typeof p.errorMessage === "string" ? { errorMessage: p.errorMessage } : {}),
		});
	});

	// Poll fallback: пропущенный socket не должен оставлять «Создаём…» до F5.
	useEffect(() => {
		let cancelled = false;
		const tick = async () => {
			if (cancelled || reconcileInFlight.current) return;
			reconcileInFlight.current = true;
			try {
				await Promise.all([
					reconcilePendingOnce({
						resolve: (jobRef, opts) => resolveRef.current(jobRef, opts),
						fail: (jobRef, hint) => failRef.current(jobRef, hint),
						isCancelled: () => cancelled,
					}),
					reconcileRichCardEntitiesOnce({ isCancelled: () => cancelled }),
				]);
			} finally {
				reconcileInFlight.current = false;
			}
		};

		const id = window.setInterval(() => {
			void tick();
		}, RECONCILE_INTERVAL_MS);
		// Первый тик чуть позже — дать socket-пути шанс без лишнего запроса.
		const first = window.setTimeout(() => {
			void tick();
		}, RECONCILE_INTERVAL_MS);

		return () => {
			cancelled = true;
			window.clearInterval(id);
			window.clearTimeout(first);
		};
	}, []);
}

/** @internal vitest */
export function resetCreateFromChatPendingForTests(): void {
	pendingByJobRef.clear();
	glossaryJobByMaterialId.clear();
}

/** @internal vitest */
export function listCreateFromChatPendingForTests(): PendingCreateFromChat[] {
	return snapshotPending();
}
